<template>
    <div class="automation-funnel-status">
        <DsPopover
            :is-open="isTooltipOpen"
            class="status-popover"
            placement="bottom"
            @close="closeTooltip"
        >
            <template #reference>
                <div
                    data-qa="funnel-status"
                    class="funnel-status"
                    :class="{ [funnelStatus]: true }"
                    @click="openTooltip"
                >
                    {{ $t(`funnel.header.status.${funnelStatus}`) }}
                </div>
            </template>

            <template #default>
                <div data-qa="funnel-status-popover" class="funnel-status-tooltip">
                    <div class="top-row">
                        <div data-qa="tooltip-text" class="tooltip-text">
                            {{ tooltipText }}
                        </div>

                        <DsIconButton class="close-tooltip-button" name="x" @click="closeTooltip" />
                    </div>
                    <div class="bottom-row">
                        <template v-if="isDraft">
                            <DsOutlineButton data-qa="open-publish-modal-button" @click="openPublishModal">
                                {{ $t('funnel.header.status.draft.publish.button.label') }}
                            </DsOutlineButton>

                            <DsTextButton data-qa="delete-automation-button" destructive @click="deleteAutomation">
                                {{ $t('funnel.header.status.draft.delete.button.label') }}
                            </DsTextButton>
                        </template>

                        <template v-else-if="isPublishedWithChanges">
                            <DsOutlineButton data-qa="open-publish-modal-button" @click="openPublishModal">
                                {{ $t('funnel.header.status.publishedWithChanges.publish.button.label') }}
                            </DsOutlineButton>

                            <DsTextButton data-qa="discard-changes-button" destructive @click="discardChanges">
                                {{ $t('funnel.header.status.publishedWithChanges.discard.button.label') }}
                            </DsTextButton>
                        </template>

                        <template v-else>
                            <DsOutlineButton @click="openUnpublishModal">
                                {{ $t('funnel.header.status.published.unpublish.button.label') }}
                            </DsOutlineButton>
                        </template>
                    </div>
                </div>
            </template>
        </DsPopover>
    </div>
</template>

<script>

import { AUTOMATION_STATUS } from '../utils/editor.constants';

export default {
    AUTOMATION_STATUS,
    props: {
        funnelStatus: {
            type: String,
            default: AUTOMATION_STATUS.DRAFT,
        },
        publishedDate: {
            type: Date,
            default: null,
        },

        publishedByUser: {
            type: String,
            default: null,
        },
    },
    emits: ['open-publish-modal', 'open-unpublish-modal', 'discard-changes', 'delete-automation'],
    data() {
        return {
            isTooltipOpen: false,
        };
    },

    computed: {
        tooltipText() {
            if (!this.isPublished) {
                return this.$t(`funnel.header.status.${this.funnelStatus}.tooltip`);
            }

            if (!this.publishedDate || !this.publishedByUser) {
                return this.$t('funnel.header.status.published.tooltip.default');
            }

            const publishDate = this.publishedDate.toLocaleDateString(navigator.language, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
            });

            const publishTime = this.publishedDate.toLocaleTimeString(navigator.language, {
                hour: 'numeric',
                minute: 'numeric',
            });

            const publishingUser = this.publishedByUser;

            return this.$t('funnel.header.status.published.tooltip', { publishDate, publishTime, publishingUser });
        },

        isDraft() {
            return this.funnelStatus === AUTOMATION_STATUS.DRAFT;
        },

        isPublishedWithChanges() {
            return this.funnelStatus === AUTOMATION_STATUS.PUBLISHED_WITH_CHANGES;
        },

        isPublished() {
            return this.funnelStatus === AUTOMATION_STATUS.PUBLISHED;
        },
    },

    methods: {
        openTooltip() {
            this.isTooltipOpen = true;
        },

        closeTooltip() {
            this.isTooltipOpen = false;
        },

        openUnpublishModal() {
            this.$emit('open-unpublish-modal');
            this.closeTooltip();
        },

        openPublishModal() {
            this.$emit('open-publish-modal');
            this.closeTooltip();
        },

        discardChanges() {
            this.$emit('discard-changes');
            this.closeTooltip();
        },

        deleteAutomation() {
            this.$emit('delete-automation');
            this.closeTooltip();
        },
    },
};
</script>

<style lang="scss" scoped>
.automation-funnel-status {
    margin: auto 0;

    .funnel-status {
        cursor: pointer;
        font-size: $font-size-xs;
        line-height: $line-height-sm;
        color: $color-ink-800;
        display: inline-block;
        border-radius: $spacing-025;
        padding: 0 $spacing-050;
        margin: auto 0;

        &.draft {
            background-color: $color-gray-700;
            color: $color-paper;
        }

        &.published {
            background-color: $color-green;
            color: $color-paper;
        }

        &.publishedWithChanges {
            color: $color-green;
        }
    }

    .status-popover {
        --popover-width: 315px;
        :deep(.popover) {
            padding: 0;
        }
    }

    .funnel-status-tooltip {
        padding: $spacing-200;

        .top-row {
            display: grid;
            grid-template-columns: 1fr auto;

            .tooltip-text {
                font-size: $font-size-md;
            }

            .close-tooltip-button {
                padding: 0;
                opacity: 0.5;
            }
        }

        .bottom-row {
            display: flex;

            .button {
                width: fit-content;
                margin: $spacing-150 $spacing-150 0 0;
            }
        }
    }
}
</style>
